"use client";

import {
  ApolloNextAppProvider,
  ApolloClient,
  InMemoryCache,
} from "@apollo/experimental-nextjs-app-support";
import { httpLink, cacheConfig } from "./apollo-config";
import { ApolloLink, from } from "@apollo/client";
import {
  CookieEnum,
  getCookie,
  getFacebookCookies,
  getTikTokCookies,
} from "@/shared/utils/cookie";
import { onError } from "@apollo/client/link/error";
import {
  clearCustomerAuthState,
  resolveOutgoingAuthToken,
} from "../Authentication/utils/tokenHelpers";
import { shouldRetryWithoutAuth } from "./auth-errors";

const localeLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      locale: getCookie(CookieEnum.LANG) || null,
    },
  }));

  return forward(operation);
});

const logoutLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    const context = operation.getContext();
    const headers = (context.headers || {}) as Record<string, string>;
    const hadAuthorization = Boolean(
      headers.authorization || headers.Authorization
    );
    const alreadyRetried = Boolean(context.retriedWithoutAuth);

    if (
      shouldRetryWithoutAuth({
        alreadyRetried,
        hadAuthorization,
        graphQLErrors,
        networkError,
      })
    ) {
      clearCustomerAuthState();
      operation.setContext(
        ({
          headers: currentHeaders = {},
        }: {
          headers?: Record<string, string>;
        }) => {
          const headersWithoutAuth = { ...currentHeaders };
          delete headersWithoutAuth.authorization;
          delete headersWithoutAuth.Authorization;
          return {
            headers: headersWithoutAuth,
            retriedWithoutAuth: true,
          };
        }
      );
      return forward(operation);
    }

    return undefined;
  }
);

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext(
    ({ headers = {} }: { headers?: Record<string, string> }) => {
      const token = resolveOutgoingAuthToken(headers);
      const headersWithoutAuth = { ...headers };
      delete headersWithoutAuth.authorization;
      delete headersWithoutAuth.Authorization;
      return {
        headers: token
          ? { ...headersWithoutAuth, authorization: `Bearer ${token}` }
          : headersWithoutAuth,
      };
    }
  );
  return forward(operation);
});

const googleAnalyticsLink = new ApolloLink((operation, forward) => {
  const facebookCookies = getFacebookCookies();
  const tiktokCookies = getTikTokCookies();
  const urlParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null;

  const ttclidFromUrl = urlParams?.get("ttclid");
  const deviceId = getCookie(CookieEnum.SESSION_ID)?.split("_")[1];
  operation.setContext(({ headers = {} }) => ({
    headers: {
      "session-id": deviceId,
      ...headers,
      "ga-id": getCookie(CookieEnum.GOOGLE_ANALYTICS) || null,
      "device-id": deviceId || null,
      ...(ttclidFromUrl ? { ttclid: ttclidFromUrl } : {}),
      ...facebookCookies,
      ...tiktokCookies,
    },
  }));

  return forward(operation);
});

function makeClient() {
  const link = from([
    logoutLink,
    authLink,
    localeLink,
    googleAnalyticsLink,
    httpLink,
  ]);

  return new ApolloClient({
    cache: new InMemoryCache(cacheConfig),
    link,
  });
}

export const ApolloProvider = ({ children }: any) => {
  return (
    <ApolloNextAppProvider makeClient={makeClient}>
      {children}
    </ApolloNextAppProvider>
  );
};
